import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Send as SendIcon,
  Notifications as NotificationIcon,
  Campaign as AnnouncementIcon,
  CheckCircle as SentIcon,
  Pending as PendingIcon,
  Drafts as DraftIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

// Types
interface NotificationListItem {
  id: number;
  title: string;
  message: string;
  type: string;
  typeColor: string;
  priority: string;
  priorityColor: string;
  status: string;
  statusColor: string;
  category: string;
  senderId: number;
  senderName: string;
  senderRole: string;
  recipientCount: number;
  readCount: number;
  unreadCount: number;
  scheduledAt: string | null;
  sentAt: string | null;
  createdAt: string;
}

interface NotificationStatistics {
  totalToday: number;
  sentToday: number;
  pendingToday: number;
  readToday: number;
  totalNotifications: number;
  sentNotifications: number;
  pendingNotifications: number;
  draftNotifications: number;
  readRate: number;
  typeDistribution: { type: string; displayName: string; count: number; color: string }[];
  priorityDistribution: { priority: string; displayName: string; count: number; color: string }[];
}

interface ComposeForm {
  title: string;
  message: string;
  type: string;
  priority: string;
  category: string;
  recipientType: string;
  sendEmail: boolean;
  sendSms: boolean;
  scheduleNow: boolean;
  scheduledAt: string;
}

interface BroadcastForm {
  title: string;
  message: string;
  type: string;
  priority: string;
  targetRoles: string[];
  sendEmail: boolean;
  sendSms: boolean;
  sendPush: boolean;
}

const initialComposeForm: ComposeForm = {
  title: '',
  message: '',
  type: 'SYSTEM',
  priority: 'NORMAL',
  category: 'GENERAL',
  recipientType: 'ALL',
  sendEmail: false,
  sendSms: false,
  scheduleNow: true,
  scheduledAt: '',
};

const initialBroadcastForm: BroadcastForm = {
  title: '',
  message: '',
  type: 'ANNOUNCEMENT',
  priority: 'NORMAL',
  targetRoles: [],
  sendEmail: true,
  sendSms: false,
  sendPush: true,
};

const NotificationsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<NotificationListItem[]>([]);
  const [statistics, setStatistics] = useState<NotificationStatistics | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<NotificationListItem | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Dialogs
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  // Forms
  const [composeForm, setComposeForm] = useState<ComposeForm>(initialComposeForm);
  const [broadcastForm, setBroadcastForm] = useState<BroadcastForm>(initialBroadcastForm);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [submitting, setSubmitting] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        page: page.toString(),
        size: rowsPerPage.toString(),
        search: searchQuery,
        type: typeFilter,
        status: statusFilter,
        priority: priorityFilter,
      });

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/notifications?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setNotifications(response.data.data.content);
        setTotalElements(response.data.data.totalElements);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, typeFilter, statusFilter, priorityFilter]);

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/notifications/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchStatistics();
  }, [fetchNotifications, fetchStatistics]);

  // Compose notification
  const handleCompose = async () => {
    if (!composeForm.title || !composeForm.message) {
      setSnackbar({ open: true, message: 'Title and message are required', severity: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/notifications`,
        {
          title: composeForm.title,
          message: composeForm.message,
          type: composeForm.type,
          priority: composeForm.priority,
          category: composeForm.category,
          recipientIds: [],
          recipientRoles: composeForm.recipientType === 'ALL' ? [] : [composeForm.recipientType],
          sendEmail: composeForm.sendEmail,
          sendSms: composeForm.sendSms,
          scheduledAt: composeForm.scheduleNow ? null : composeForm.scheduledAt,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSnackbar({ open: true, message: 'Notification created successfully', severity: 'success' });
        setComposeDialogOpen(false);
        setComposeForm(initialComposeForm);
        fetchNotifications();
        fetchStatistics();
      } else {
        setSnackbar({ open: true, message: response.data.message || 'Failed to create notification', severity: 'error' });
      }
    } catch (error: any) {
      console.error('Error creating notification:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to create notification', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Send broadcast
  const handleBroadcast = async () => {
    if (!broadcastForm.title || !broadcastForm.message) {
      setSnackbar({ open: true, message: 'Title and message are required', severity: 'error' });
      return;
    }

    if (broadcastForm.targetRoles.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one target role', severity: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/notifications/broadcast`,
        {
          title: broadcastForm.title,
          message: broadcastForm.message,
          type: broadcastForm.type,
          priority: broadcastForm.priority,
          targetRoles: broadcastForm.targetRoles,
          sendEmail: broadcastForm.sendEmail,
          sendSms: broadcastForm.sendSms,
          sendPush: broadcastForm.sendPush,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSnackbar({ open: true, message: 'Broadcast sent successfully', severity: 'success' });
        setBroadcastDialogOpen(false);
        setBroadcastForm(initialBroadcastForm);
        fetchNotifications();
        fetchStatistics();
      } else {
        setSnackbar({ open: true, message: response.data.message || 'Failed to send broadcast', severity: 'error' });
      }
    } catch (error: any) {
      console.error('Error sending broadcast:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to send broadcast', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // View notification detail
  const handleViewNotification = async (notification: NotificationListItem) => {
    setSelectedNotification(notification);
    setViewDialogOpen(true);
    setActionAnchorEl(null);
  };

  const handleActionOpen = (event: React.MouseEvent<HTMLElement>, notification: NotificationListItem) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'SYSTEM': return <NotificationIcon />;
      case 'ANNOUNCEMENT': return <AnnouncementIcon />;
      case 'ALERT': return <NotificationIcon />;
      case 'REMINDER': return <SentIcon />;
      default: return <NotificationIcon />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SENT': return <SentIcon />;
      case 'PENDING': return <PendingIcon />;
      case 'DRAFT': return <DraftIcon />;
      default: return <PendingIcon />;
    }
  };

  const statCards = statistics ? [
    { title: "Today's Notifications", value: statistics.totalToday, icon: <NotificationIcon />, color: '#2563eb' },
    { title: 'Sent', value: statistics.sentToday, icon: <SendIcon />, color: '#10b981' },
    { title: 'Pending', value: statistics.pendingToday, icon: <PendingIcon />, color: '#f59e0b' },
    { title: 'Read Rate', value: `${statistics.readRate.toFixed(1)}%`, icon: <ViewIcon />, color: '#8b5cf6' },
    { title: 'Total', value: statistics.totalNotifications, icon: <AnnouncementIcon />, color: '#06b6d4' },
    { title: 'Draft', value: statistics.draftNotifications, icon: <DraftIcon />, color: '#64748b' },
  ] : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Notification & Communication Center
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Manage announcements, alerts, and notifications
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setComposeDialogOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Compose
          </Button>
          <Button
            variant="contained"
            startIcon={<AnnouncementIcon />}
            onClick={() => setBroadcastDialogOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Broadcast
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchNotifications(); fetchStatistics(); }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mt: 0.5 }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: `${stat.color}15`,
                    color: stat.color,
                  }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Type Distribution */}
      {statistics && statistics.typeDistribution.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Notification Types
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {statistics.typeDistribution.map((item, index) => (
                <Chip
                  key={index}
                  icon={getTypeIcon(item.type)}
                  label={`${item.displayName}: ${item.count}`}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontWeight: 500,
                    bgcolor: `${item.color}15`,
                    color: item.color,
                    border: `1px solid ${item.color}40`,
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Tab label={`All (${totalElements})`} sx={{ textTransform: 'none' }} />
          <Tab label="Sent" icon={<SentIcon />} sx={{ textTransform: 'none' }} />
          <Tab label="Pending" icon={<PendingIcon />} sx={{ textTransform: 'none' }} />
          <Tab label="Draft" icon={<DraftIcon />} sx={{ textTransform: 'none' }} />
          <Tab label="Announcements" icon={<AnnouncementIcon />} sx={{ textTransform: 'none' }} />
        </Tabs>

        {/* Search & Filter */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
          <TextField
            size="small"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="SYSTEM">System</MenuItem>
              <MenuItem value="ANNOUNCEMENT">Announcement</MenuItem>
              <MenuItem value="ALERT">Alert</MenuItem>
              <MenuItem value="REMINDER">Reminder</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="SENT">Sent</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="DRAFT">Draft</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="NORMAL">Normal</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="URGENT">Urgent</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Recipients</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Read</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Sent At</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : notifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography sx={{ color: '#64748b' }}>No notifications found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                notifications.map((notification) => (
                  <TableRow key={notification.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {notification.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {notification.message?.substring(0, 50)}...
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getTypeIcon(notification.type)}
                        label={notification.type}
                        size="small"
                        sx={{
                          bgcolor: `${notification.typeColor}15`,
                          color: notification.typeColor,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={notification.priority}
                        size="small"
                        sx={{
                          bgcolor: `${notification.priorityColor}15`,
                          color: notification.priorityColor,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(notification.status)}
                        label={notification.status}
                        size="small"
                        sx={{
                          bgcolor: `${notification.statusColor}15`,
                          color: notification.statusColor,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {notification.recipientCount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#10b981' }}>
                          {notification.readCount}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          /
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#f59e0b' }}>
                          {notification.unreadCount}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {notification.sentAt ? new Date(notification.sentAt).toLocaleDateString() : 'Scheduled'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleActionOpen(e, notification)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => selectedNotification && handleViewNotification(selectedNotification)}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Send Now</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Compose Dialog */}
      <Dialog open={composeDialogOpen} onClose={() => setComposeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Compose Notification</Typography>
          <IconButton onClick={() => setComposeDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={composeForm.title}
                onChange={(e) => setComposeForm({ ...composeForm, title: e.target.value })}
                placeholder="Enter notification title"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message"
                value={composeForm.message}
                onChange={(e) => setComposeForm({ ...composeForm, message: e.target.value })}
                placeholder="Enter notification message"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={composeForm.type}
                  label="Type"
                  onChange={(e) => setComposeForm({ ...composeForm, type: e.target.value })}
                >
                  <MenuItem value="SYSTEM">System</MenuItem>
                  <MenuItem value="ANNOUNCEMENT">Announcement</MenuItem>
                  <MenuItem value="ALERT">Alert</MenuItem>
                  <MenuItem value="REMINDER">Reminder</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={composeForm.priority}
                  label="Priority"
                  onChange={(e) => setComposeForm({ ...composeForm, priority: e.target.value })}
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="NORMAL">Normal</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="URGENT">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={composeForm.category}
                  label="Category"
                  onChange={(e) => setComposeForm({ ...composeForm, category: e.target.value })}
                >
                  <MenuItem value="GENERAL">General</MenuItem>
                  <MenuItem value="APPOINTMENT">Appointment</MenuItem>
                  <MenuItem value="SYSTEM">System</MenuItem>
                  <MenuItem value="FEATURE">Feature</MenuItem>
                  <MenuItem value="EMERGENCY">Emergency</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Recipients</InputLabel>
                <Select
                  value={composeForm.recipientType}
                  label="Recipients"
                  onChange={(e) => setComposeForm({ ...composeForm, recipientType: e.target.value })}
                >
                  <MenuItem value="ALL">All Users</MenuItem>
                  <MenuItem value="GOVERNMENT_DOCTOR">Doctors Only</MenuItem>
                  <MenuItem value="IAS_OFFICER">IAS Officers</MenuItem>
                  <MenuItem value="IPS_OFFICER">IPS Officers</MenuItem>
                  <MenuItem value="CIVILHEALTH_COORDINATOR">Coordinators</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel
                  control={<Checkbox checked={composeForm.sendEmail} onChange={(e) => setComposeForm({ ...composeForm, sendEmail: e.target.checked })} />}
                  label="Send via Email"
                />
                <FormControlLabel
                  control={<Checkbox checked={composeForm.sendSms} onChange={(e) => setComposeForm({ ...composeForm, sendSms: e.target.checked })} />}
                  label="Send via SMS"
                />
                <FormControlLabel
                  control={<Checkbox checked={composeForm.scheduleNow} onChange={(e) => setComposeForm({ ...composeForm, scheduleNow: e.target.checked })} />}
                  label="Send Immediately"
                />
              </FormGroup>
            </Grid>
            {!composeForm.scheduleNow && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Schedule At"
                  value={composeForm.scheduledAt}
                  onChange={(e) => setComposeForm({ ...composeForm, scheduledAt: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setComposeDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleCompose} disabled={submitting} sx={{ textTransform: 'none' }}>
            {submitting ? 'Sending...' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastDialogOpen} onClose={() => setBroadcastDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Send Broadcast</Typography>
          <IconButton onClick={() => setBroadcastDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Broadcast messages are sent to all users matching the selected roles.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={broadcastForm.title}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                placeholder="Enter broadcast title"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message"
                value={broadcastForm.message}
                onChange={(e) => setBroadcastForm({ ...broadcastForm, message: e.target.value })}
                placeholder="Enter broadcast message"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={broadcastForm.type}
                  label="Type"
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, type: e.target.value })}
                >
                  <MenuItem value="ANNOUNCEMENT">Announcement</MenuItem>
                  <MenuItem value="SYSTEM">System</MenuItem>
                  <MenuItem value="ALERT">Alert</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={broadcastForm.priority}
                  label="Priority"
                  onChange={(e) => setBroadcastForm({ ...broadcastForm, priority: e.target.value })}
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="NORMAL">Normal</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="URGENT">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Target Roles</Typography>
              <FormGroup row>
                {['GOVERNMENT_DOCTOR', 'IAS_OFFICER', 'IPS_OFFICER', 'IFS_OFFICER', 'IRS_OFFICER', 'JUDGE', 'MINISTER', 'OSD', 'SENIOR_GOVERNMENT_OFFICER', 'CIVILHEALTH_COORDINATOR', 'SYSTEM_ADMIN'].map((role) => (
                  <FormControlLabel
                    key={role}
                    control={
                      <Checkbox
                        checked={broadcastForm.targetRoles.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBroadcastForm({ ...broadcastForm, targetRoles: [...broadcastForm.targetRoles, role] });
                          } else {
                            setBroadcastForm({ ...broadcastForm, targetRoles: broadcastForm.targetRoles.filter((r) => r !== role) });
                          }
                        }}
                      />
                    }
                    label={role.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                  />
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Delivery Channels</Typography>
              <FormGroup row>
                <FormControlLabel
                  control={<Checkbox checked={broadcastForm.sendEmail} onChange={(e) => setBroadcastForm({ ...broadcastForm, sendEmail: e.target.checked })} />}
                  label="Email"
                />
                <FormControlLabel
                  control={<Checkbox checked={broadcastForm.sendSms} onChange={(e) => setBroadcastForm({ ...broadcastForm, sendSms: e.target.checked })} />}
                  label="SMS"
                />
                <FormControlLabel
                  control={<Checkbox checked={broadcastForm.sendPush} onChange={(e) => setBroadcastForm({ ...broadcastForm, sendPush: e.target.checked })} />}
                  label="Push Notification"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBroadcastDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleBroadcast} disabled={submitting} sx={{ textTransform: 'none' }}>
            {submitting ? 'Sending...' : 'Send Broadcast'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Notification Details</Typography>
          <IconButton onClick={() => setViewDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {selectedNotification && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Title</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedNotification.title}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Message</Typography>
                <Typography variant="body1">{selectedNotification.message}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Type</Typography>
                <Chip
                  icon={getTypeIcon(selectedNotification.type)}
                  label={selectedNotification.type}
                  size="small"
                  sx={{ mt: 0.5, bgcolor: `${selectedNotification.typeColor}15`, color: selectedNotification.typeColor }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Priority</Typography>
                <Chip
                  label={selectedNotification.priority}
                  size="small"
                  sx={{ mt: 0.5, bgcolor: `${selectedNotification.priorityColor}15`, color: selectedNotification.priorityColor }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Status</Typography>
                <Chip
                  icon={getStatusIcon(selectedNotification.status)}
                  label={selectedNotification.status}
                  size="small"
                  sx={{ mt: 0.5, bgcolor: `${selectedNotification.statusColor}15`, color: selectedNotification.statusColor }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Recipients</Typography>
                <Typography variant="body1">{selectedNotification.recipientCount} users</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Read Rate</Typography>
                <Typography variant="body1">
                  {selectedNotification.readCount} / {selectedNotification.recipientCount} ({selectedNotification.recipientCount > 0 ? ((selectedNotification.readCount / selectedNotification.recipientCount) * 100).toFixed(0) : 0}%)
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Created</Typography>
                <Typography variant="body1">{new Date(selectedNotification.createdAt).toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" sx={{ color: '#64748b' }}>Sender</Typography>
                <Typography variant="body1">{selectedNotification.senderName} ({selectedNotification.senderRole})</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsPage;